import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { ANAESTHESIA_TYPE_LABELS } from '../shared/constants';

const useStyles = makeStyles(() => ({
  root: { padding: 24 },
  sectionTitle: { fontSize: '1.05rem', fontWeight: 600, color: '#333', marginBottom: 16 },
  timer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    marginBottom: 24,
  },
  timerLabel: { fontSize: '0.88rem', fontWeight: 600, color: '#2E7D32' },
  timerValue: { fontSize: '1.4rem', fontWeight: 700, color: '#1B5E20', fontFamily: 'monospace' },
  whoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    padding: '12px 16px',
    backgroundColor: '#fafafa',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
  },
  whoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  dot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    display: 'inline-block',
  },
  dotGreen: { backgroundColor: '#4CAF50' },
  dotGray: { backgroundColor: '#BDBDBD' },
  whoLabel: { fontSize: '0.85rem', fontWeight: 500, color: '#555' },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  infoLabel: { fontWeight: 600, fontSize: '0.88rem', color: '#444', minWidth: 140 },
  infoValue: { fontSize: '0.92rem', color: '#333' },
  placeholder: {
    padding: 32,
    textAlign: 'center',
    color: '#999',
    border: '2px dashed #e0e0e0',
    borderRadius: 8,
    minHeight: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  divider: {
    height: 1,
    backgroundColor: '#e0e0e0',
    margin: '20px 0',
  },
  endBtn: {
    marginTop: 16,
    backgroundColor: '#D32F2F',
    color: '#fff',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#B71C1C' },
  },
}));

const DayCaseProcedure = ({ surgery }) => {
  const classes = useStyles();

  const safeAnaesthesia = surgery && surgery.anaesthesiaType
    ? (ANAESTHESIA_TYPE_LABELS[surgery.anaesthesiaType] || surgery.anaesthesiaType)
    : 'Local';

  const safeProcedure = surgery && surgery.procedure ? surgery.procedure : 'Day-Case Procedure';

  return (
    <Paper className={classes.root} elevation={0}>
      <Typography className={classes.sectionTitle}>Intra-Operative View</Typography>

      {/* Timer */}
      <div className={classes.timer}>
        <Typography className={classes.timerLabel}>Procedure Duration:</Typography>
        <Typography className={classes.timerValue}>00:00:00</Typography>
      </div>

      {/* WHO Checklist Summary */}
      <div className={classes.whoRow}>
        <Typography style={{ fontWeight: 600, fontSize: '0.88rem', color: '#333', marginRight: 8 }}>
          WHO Checklist:
        </Typography>
        <div className={classes.whoItem}>
          <span className={`${classes.dot} ${classes.dotGreen}`} />
          <Typography className={classes.whoLabel}>Sign-In</Typography>
        </div>
        <div className={classes.whoItem}>
          <span className={`${classes.dot} ${classes.dotGreen}`} />
          <Typography className={classes.whoLabel}>Time-Out</Typography>
        </div>
        <div className={classes.whoItem}>
          <span className={`${classes.dot} ${classes.dotGray}`} />
          <Typography className={classes.whoLabel}>Sign-Out</Typography>
        </div>
      </div>

      {/* Procedure Info */}
      <div className={classes.infoRow}>
        <Typography className={classes.infoLabel}>Procedure:</Typography>
        <Typography className={classes.infoValue}>{safeProcedure}</Typography>
      </div>
      <div className={classes.infoRow}>
        <Typography className={classes.infoLabel}>Anaesthesia Type:</Typography>
        <Typography className={classes.infoValue}>{safeAnaesthesia}</Typography>
      </div>

      <div className={classes.divider} />

      {/* Placeholder Sections */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <div className={classes.placeholder}>
            <Typography variant="body2" color="textSecondary">
              Operative Notes will render here
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.placeholder}>
            <Typography variant="body2" color="textSecondary">
              Consumables Tracker will render here
            </Typography>
          </div>
        </Grid>
      </Grid>

      {/* End Procedure */}
      <Button className={classes.endBtn} variant="contained">
        End Procedure
      </Button>
    </Paper>
  );
};

export default DayCaseProcedure;
